import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Pill, Activity, Heart, Droplet, Scan, Scissors,
  Microscope, Stethoscope, ChevronRight, ArrowUpRight,
  CheckCircle2, PhoneCall, Home
} from 'lucide-react';

const Services = () => {

  const services = [
    {
      icon: Scan,
      title: 'RIRS (Retrograde Intrarenal Surgery)',
      description:
        'Advanced laser treatment for kidney stones using flexible ureteroscopy — a scarless procedure associated with faster recovery and minimal hospitalization.',
      whoShouldConsult:
        'Patients with kidney stones seeking minimally invasive, no-incision treatment options.',
      color: 'from-cyan-600 to-blue-500'
    },
    {
      icon: Pill,
      title: 'Kidney Stone Treatment',
      description:
        'Comprehensive kidney stone management including laser stone surgery, ESWL (Extracorporeal Shock Wave Lithotripsy), PCNL (Percutaneous Nephrolithotomy), and minimally invasive endoscopic procedures.',
      whoShouldConsult:
        'Patients experiencing severe flank pain, blood in urine, painful urination, or diagnosed kidney stones.',
      color: 'from-blue-500 to-cyan-400'
    },
    {
      icon: Activity,
      title: 'Prostate Disorders',
      description:
        'Comprehensive evaluation and treatment of benign prostatic hyperplasia (BPH), prostatitis, and prostate cancer, including TURP and laser-based procedures when clinically indicated.',
      whoShouldConsult:
        'Men experiencing difficulty urinating, frequent urination, weak urine flow, or elevated PSA levels.',
      color: 'from-teal-500 to-emerald-400'
    },
    {
      icon: Microscope,
      title: 'Endourology',
      description:
        'Minimally invasive endoscopic procedures for kidney stones, ureteral strictures, and selected urological tumors, enabling precise treatment with reduced recovery time.',
      whoShouldConsult:
        'Patients requiring minimally invasive treatment for stones, strictures, or other urinary tract conditions.',
      color: 'from-cyan-500 to-blue-400'
    },
    {
      icon: Stethoscope,
      title: 'Laparoscopic Urology',
      description:
        'Advanced laparoscopic procedures for selected urological conditions offering faster recovery and minimal scarring.',
      whoShouldConsult:
        'Patients requiring surgical intervention who prefer minimally invasive laparoscopic options.',
      color: 'from-blue-700 to-teal-500'
    },
    {
      icon: Heart,
      title: 'Male Infertility',
      description:
        'Complete male fertility evaluation including semen analysis, hormonal assessment, and treatment options such as varicocele repair and medical therapy.',
      whoShouldConsult:
        'Couples facing difficulty conceiving after one year, or men with known fertility concerns.',
      color: 'from-rose-500 to-orange-400'
    },
    {
      icon: Droplet,
      title: 'Erectile Dysfunction',
      description:
        'Confidential evaluation and treatment including lifestyle guidance, medical therapy, intracavernosal injections, and penile implant counseling when clinically indicated.',
      whoShouldConsult:
        'Men experiencing persistent difficulty achieving or maintaining erections.',
      color: 'from-indigo-500 to-purple-400'
    },
    {
      icon: Scan,
      title: 'Urinary Tract Infections',
      description:
        'Diagnosis and management of recurrent and complicated urinary infections with preventive care and anatomical evaluation when necessary.',
      whoShouldConsult:
        'Patients with frequent infections, burning urination, fever, or blood in urine.',
      color: 'from-blue-600 to-indigo-400'
    },
    {
      icon: Scissors,
      title: 'Kidney Transplant Evaluation & Urological Support',
      description:
        'Pre-transplant urological evaluation, surgical clearance, and post-transplant urological care in coordination with transplant teams.',
      whoShouldConsult:
        'Patients with advanced kidney disease, declining renal function, or those preparing for transplant.',
      color: 'from-teal-600 to-blue-500'
    },
  ];

  return (
    <div className="pt-20 bg-[#f8fafc] overflow-hidden">

      {/* HERO */}
      <section className="bg-gradient-to-br from-blue-900 to-teal-800 py-14 relative">

        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-teal-400/10 rounded-full -ml-10 -mb-10 blur-2xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          <nav className="flex items-center gap-2 text-teal-200/80 text-sm font-bold uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" /> Home
            </Link>
            <ChevronRight className="w-4 h-4 opacity-50" />
            <span className="text-white">Urology Services</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
            Advanced Urology Care
          </h1>

          <div className="h-1.5 w-24 bg-teal-400 mt-4 rounded-full" />

          <p className="text-blue-100/80 mt-6 max-w-2xl font-medium text-lg leading-relaxed">
            Comprehensive diagnosis and modern treatment for kidney stones, prostate disorders,
            male infertility, and complex urological conditions using evidence-based medical care.
          </p>

        </div>
      </section>


      {/* SERVICES GRID */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  key={index}
                  className="group bg-white rounded-[3rem] p-10 border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-blue-200/40 transition-all duration-500 relative overflow-hidden"
                >

                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-bl-[100px]`} />

                  <div className="flex flex-col md:flex-row gap-8 relative z-10">

                    <div className={`w-20 h-20 bg-gradient-to-br ${service.color} rounded-[2rem] flex items-center justify-center shrink-0 shadow-lg group-hover:rotate-6 transition-transform duration-500`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>

                    <div className="flex-grow">

                      <h3 className="text-2xl font-black text-slate-900 mb-4 flex items-center justify-between">
                        {service.title}
                        <ArrowUpRight className="w-6 h-6 text-slate-200 group-hover:text-blue-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                      </h3>

                      <p className="text-slate-500 font-medium leading-relaxed mb-6">
                        {service.description}
                      </p>

                      <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 group-hover:bg-blue-50/50 group-hover:border-blue-100 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-500" />
                          <h4 className="font-black text-[10px] uppercase tracking-widest text-slate-400">
                            Who Should Consult:
                          </h4>
                        </div>

                        <p className="text-sm font-bold text-slate-700 leading-snug">
                          {service.whoShouldConsult}
                        </p>
                      </div>

                      <Link to="/appointment" className="mt-8 inline-flex items-center gap-2 text-blue-600 font-black text-sm uppercase tracking-wider group/link">
                        Book Consultation
                        <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center group-hover/link:bg-blue-600 group-hover/link:text-white transition-all">
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </Link>

                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="bg-gradient-to-r from-blue-900 to-teal-800 rounded-[4rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">

            <div className="absolute top-0 right-0 p-10 opacity-10">
              <PhoneCall className="w-40 h-40 text-white" />
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Need Expert Urological Care?
              </h2>

              <p className="text-blue-100 text-xl font-medium mb-12 max-w-2xl mx-auto">
                Schedule your consultation with Dr. Sachitanand Chaurasia today.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/appointment" className="bg-white text-blue-900 px-12 py-5 rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-xl">
                  Book Appointment
                </Link>

                <Link to="/contact" className="bg-teal-500/20 backdrop-blur-md border-2 border-teal-500/30 text-white px-12 py-5 rounded-2xl font-black text-lg hover:bg-teal-500 transition-all">
                  Contact
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Services;
